import React, { ReactNode, useEffect, useRef, useState } from 'react';
import {
  Animated,
  Easing,
  Platform,
  StyleProp,
  TextInput,
  TouchableWithoutFeedback,
  View,
  ViewStyle,
} from 'react-native';
import { MultiSelect } from 'react-native-element-dropdown';
import colors from '@/config/colors';
import Ionicons from '@expo/vector-icons/Ionicons';
import { Text } from '@/components/Text';
import { styles } from '@/components/ui/therapist/Dropdown/style';
import { useOverlayGuard } from '@/hooks/useOverlayGuard';

export type DataType = {
  label: string;
  value: string;
  type?: string;
};

export type DropdownPositionType = 'auto' | 'top' | 'bottom';

type DropdownProps = {
  search?: boolean;
  data: DataType[];
  additionalInfo?: string;
  label?: string;
  required?: boolean;
  renderOption?: () => React.JSX.Element;
  status?: 'warning' | 'validated';
  valueField?: string;
  labelField?: string;
  placeholder?: string;
  multiSelect?: boolean;
  onSelectChange: (items: string[]) => void;
  disabled?: boolean;
  value?: string[];
  customStyles?: StyleProp<ViewStyle>;
  legendIcon?: ReactNode;
  itemTypeToMatchLegendIcon?: string;
  allowRemoveItems?: boolean;
  dropdownPosition?: DropdownPositionType;
};

const Dropdown = ({
  additionalInfo,
  data,
  label,
  required,
  renderOption,
  status,
  valueField = 'value',
  labelField = 'label',
  placeholder = 'Auswählen',
  multiSelect = false,
  search = false,
  onSelectChange,
  disabled = false,
  value,
  customStyles,
  legendIcon,
  itemTypeToMatchLegendIcon,
  allowRemoveItems,
  dropdownPosition = 'auto',
}: DropdownProps) => {
  const containerRef = useRef<View>(null);
  const [dropdownWidth, setDropdownWidth] = useState<number | undefined>(
    undefined,
  );
  const isWeb = Platform.OS === 'web';
  const [dropdownKey, setDropdownKey] = useState(0);
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [isTyping, setIsTyping] = useState<boolean>(false);
  const [selected, setSelected] = useState<string[]>(
    value?.length ? value : [],
  );
  const [searchValue, setSearchValue] = useState<string>('');

  useEffect(() => {
    setSelected(value?.length ? value : []);
  }, [value]);

  useOverlayGuard(isFocused);
  const rotateAnim = useRef(new Animated.Value(0)).current;
  const isActive = isTyping;
  const isWarning = status === 'warning' && !isActive;
  const isValidated = status === 'validated' && !isActive;

  const handleFocus = () => setIsFocused(true);
  const handleBlur = () => {
    setIsFocused(false);
    setIsTyping(false);
  };

  const outerStyles = [
    styles.outer,
    isFocused || isActive ? styles.outerFocused : {},
  ];

  const innerStyles = [
    styles.inner,
    { paddingBottom: isWeb ? 8 : 4 },
    {
      borderBottomColor:
        status === 'validated'
          ? colors.newUI.therapist.green70
          : status === 'warning'
            ? colors.newUI.therapist.red70
            : colors.newUI.therapist.primaryBlue80,
    },
  ];

  const renderItem = (item: DataType, selected?: boolean) => {
    const isFirstItem = item.value === data[0]?.value;
    const itemContainerStyle = [
      styles.itemContainer,
      isFirstItem && {
        borderTopWidth: 1,
        borderTopColor: colors.newUI.therapist.primaryBlue10,
      },
    ];

    return (
      <>
        {item.type ? (
          <TouchableWithoutFeedback onPress={() => {}}>
            <View style={[...itemContainerStyle, styles.disabledItemWrapper]}>
              <Text style={styles.disabledItem}>
                {legendIcon &&
                  item.type === itemTypeToMatchLegendIcon &&
                  legendIcon}
                {item.label}
              </Text>
            </View>
          </TouchableWithoutFeedback>
        ) : (
          <View style={itemContainerStyle}>
            <View style={styles.labelWrapper}>
              <Text
                style={
                  multiSelect
                    ? [
                        styles.itemLabelMultiSelect,
                        selected && styles.itemLabelSelected,
                      ]
                    : [styles.itemLabel]
                }
              >
                {item.label}
              </Text>
            </View>

            {selected && (
              <Ionicons
                name="checkmark"
                size={30}
                color="green"
                style={styles.checkIcon}
              />
            )}
          </View>
        )}
      </>
    );
  };

  const rotate = rotateAnim.interpolate({
    inputRange: [0, 1],
    outputRange: ['0deg', '180deg'],
  });

  const renderRightIcon = () => (
    <View style={styles.rightIconWrapper}>
      {isValidated && (
        <View
          style={[
            styles.statusCircle,
            { backgroundColor: colors.newUI.therapist.green70 },
          ]}
        >
          <Ionicons name="checkmark" size={14} color="#fff" />
        </View>
      )}
      {isWarning && (
        <View
          style={[
            styles.statusCircle,
            { backgroundColor: colors.newUI.therapist.red70 },
          ]}
        >
          <Ionicons name="alert" size={14} color="#fff" />
        </View>
      )}
      <View style={[styles.iconWrapper, { height: isWeb ? 58 : 56 }]}>
        <Animated.View style={{ transform: [{ rotate }] }}>
          <Ionicons
            name="chevron-down-outline"
            size={18}
            color={colors.newUI.therapist.primaryBlue80}
          />
        </Animated.View>
      </View>
    </View>
  );

  const customOnChange = (items: string[]) => {
    let selectedItems = [];

    if (multiSelect) {
      selectedItems = items;
    } else {
      if (items.length === 0) {
        selectedItems = value ?? [];
      } else {
        const lastSelected = items.at(-1);
        selectedItems = lastSelected ? [lastSelected] : [];
      }
      handleBlur();
    }
    setSelected(selectedItems);
    onSelectChange(selectedItems);
  };

  const renderSelectedItem = (item: any, unSelect?: (item: any) => void) => (
    <View
      style={
        multiSelect ? styles.selectedStyleMultiSelect : styles.selectedStyle
      }
    >
      <Text style={styles.selectedTextStyle}>{item[labelField]}</Text>
      {allowRemoveItems && (
        <TouchableWithoutFeedback onPress={() => unSelect && unSelect(item)}>
          <View>
            <Ionicons
              name="close"
              size={16}
              color={colors.newUI.therapist.primaryBlue80}
            />
          </View>
        </TouchableWithoutFeedback>
      )}
    </View>
  );

  useEffect(() => {
    Animated.timing(rotateAnim, {
      toValue: isFocused ? 1 : 0,
      duration: 200,
      easing: Easing.inOut(Easing.ease),
      useNativeDriver: true,
    }).start();
  }, [isFocused]);

  useEffect(() => {
    // this is to trick the component to close when a user selects an item
    // the multiselect component does not have a manual onClose prop to trigger it to close
    if (!multiSelect) {
      setDropdownKey((prev) => prev + 1);
    }
  }, [multiSelect, selected]);

  return (
    <View
      style={[styles.container, disabled && { opacity: 0.5 }, customStyles]}
      ref={containerRef}
      onLayout={(event) => {
        const { width } = event.nativeEvent.layout;
        setDropdownWidth(width);
      }}
    >
      {label && (
        <Text style={styles.label}>
          {label}
          {required && <Text style={styles.required}> *</Text>}
        </Text>
      )}
      {additionalInfo && <Text style={styles.info}>{additionalInfo}</Text>}

      <View style={outerStyles}>
        <View style={innerStyles}>
          <MultiSelect
            search={search}
            key={dropdownKey}
            style={styles.dropdown}
            placeholderStyle={styles.placeholderStyle}
            selectedTextProps={{
              allowFontScaling: false,
              maxFontSizeMultiplier: 0.5,
            }}
            selectedTextStyle={styles.selectedTextStyle}
            inputSearchStyle={styles.inputSearchStyle}
            searchPlaceholderTextColor={colors.newUI.therapist.grey70}
            inside
            iconStyle={styles.iconStyle}
            data={data}
            disable={disabled}
            labelField={labelField}
            valueField={valueField}
            placeholder={placeholder}
            searchPlaceholder="Suchen..."
            value={selected}
            onChange={customOnChange}
            onChangeText={() => setIsTyping(true)}
            onFocus={handleFocus}
            onBlur={handleBlur}
            containerStyle={[
              styles.containerStyle,
              dropdownWidth && dropdownWidth > 0
                ? { width: dropdownWidth, marginLeft: -11 }
                : null,
            ]}
            renderItem={renderOption ? renderOption : renderItem}
            renderRightIcon={renderRightIcon}
            renderInputSearch={(onSearch) => (
              <TextInput
                value={searchValue}
                placeholder="Suchen..."
                placeholderTextColor={colors.newUI.therapist.grey70}
                autoFocus={true}
                style={[styles.itemLabel]}
                onChange={(e) => {
                  setSearchValue(e.nativeEvent.text);
                  onSearch(e.nativeEvent.text);
                }}
                allowFontScaling={false}
              />
            )}
            renderSelectedItem={renderSelectedItem}
            dropdownPosition={dropdownPosition}
          />
        </View>
      </View>
    </View>
  );
};

export default Dropdown;
